import { useState } from 'react';

import { GrayColors, typographyStyle, Input, Button, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast, Typography } from '@revenuemarket/ui';
import { extractPartnerDomain } from '@revenuemarket/utils/partners/getPartnerByHostname';
import { Form } from 'antd';
import { Rule } from 'antd/es/form';
import { setCookies } from 'cookies-next';
import { useRouter } from 'next/router';
import * as yup from 'yup';

import { getDomainForCookie } from '~apis/axios/instance';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useSendResetPasswordPasscode } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';

export type ResetPasswordEmailStepModel = { email: string } | undefined;

interface ResetPasswordEmailStepForm {
  email: string;
}

const ResetPasswordEmailStepScheme = yup.object({
  email: COMMON_YUP.email,
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await ResetPasswordEmailStepScheme.validateAt(field, { [field]: value });
  },
});

interface Props {
  title: string;
  prefilledEmail?: string;
  model: ResetPasswordEmailStepModel;
  onFinish: (values: ResetPasswordEmailStepForm) => void;
}

const ResetPasswordEmailStep = ({
  title,
  prefilledEmail,
  model,
  onFinish: onFinishProp,
}: Props) => {
  const router = useRouter();

  const [form] = Form.useForm<ResetPasswordEmailStepForm>();
  const { mutateAsync: sendResetPasswordPasscode, isLoading: isResetPasswordPasscodeLoading } =
    useSendResetPasswordPasscode();

  const [showSignupGuide, setShowSignupGuide] = useState(false);
  const [notFoundEmail, setNotFoundEmail] = useState('');

  const onFinish = async (values: ResetPasswordEmailStepForm) => {
    const partnerDomain = extractPartnerDomain(window?.location.hostname);

    const response = await sendResetPasswordPasscode({
      ...values,
      partnerDomain,
    });
    if (response.Status === ResponseStatus.FAILURE) {
      switch (response.ErrorCode) {
        case 'USER_NOT_FOUND':
          setShowSignupGuide(true);
          setNotFoundEmail(values.email);
          break;
        default:
          Toast.error('오류가 발생했습니다.');
          break;
      }
      return;
    }
    setShowSignupGuide(false);
    setNotFoundEmail('');
    onFinishProp(values);
  };

  return (
    <Flex align="center" direction="column">
      <Flex
        direction="column"
        gap={12}
        className={css`
          width: 440px;

          @media (max-width: 768px) {
            width: 100%;
            max-width: 440px;
          }
        `}
      >
        <Typography
          variant="body1"
          style={{
            ...typographyStyle['24_600'],
            color: GrayColors.gray900,
            paddingBottom: 12,
          }}
        >
          {title}
        </Typography>
        <Form
          form={form}
          onFinish={onFinish}
          initialValues={{
            ...model,
            email: prefilledEmail,
          }}
        >
          <Form.Item
            name="email"
            rules={[getSchemeRule('email')]}
            style={{ marginBottom: 12 }}
            className={css`
              &&& .ant-form-item-explain {
                margin-bottom: 12px;
              }
            `}
          >
            <Input
              inputSize="xlarge"
              type="email"
              placeholder="이메일을 입력해주세요"
              disabled={prefilledEmail !== undefined}
              onChange={() => {
                setShowSignupGuide(false);
                setNotFoundEmail('');
              }}
            />
          </Form.Item>
          <Form.Item noStyle dependencies={['email']}>
            {({ getFieldsValue }) => {
              const valid = (() => {
                try {
                  ResetPasswordEmailStepScheme.validateSync(getFieldsValue());
                  return true;
                } catch {
                  return false;
                }
              })();

              return (
                <Button
                  buttonType="primary"
                  buttonSize="xlarge"
                  disable={!valid && prefilledEmail === undefined}
                  loading={isResetPasswordPasscodeLoading}
                  onClick={form.submit}
                  className={css`
                    width: 100%;
                  `}
                >
                  인증번호 발송
                </Button>
              );
            }}
          </Form.Item>
        </Form>

        {showSignupGuide ? (
          <Flex direction="column" gap={8}>
            <Typography
              variant="body2"
              style={{
                ...typographyStyle['16_400'],
                color: GrayColors.gray700,
              }}
            >
              이메일 주소로 등록된 계정이 없어요
            </Typography>
            <Button
              buttonType="default"
              buttonSize="large"
              onClick={async () => {
                setCookies('email-from-landing', notFoundEmail, {
                  domain: getDomainForCookie(),
                });
                await router.push('/auth/signup');
              }}
              border={true}
            >
              가입하기
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default ResetPasswordEmailStep;
